import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useConfirmationStore from '@/stores/Confirmation';
import { Team } from '@/types';

import { useDeleteTeam } from '../../api/deleteTeam';

import { SettingsHeader } from './common-settings';
import { ViewId } from './team-settings';

interface DangerZoneViewProps {
  team: Team;
  onOpenChange: (open: boolean) => void;
  setActiveView: React.Dispatch<React.SetStateAction<ViewId>>;
}

export function DangerZoneView({
  team,
  onOpenChange,
  setActiveView,
}: DangerZoneViewProps) {
  const deleteTeamMutation = useDeleteTeam();
  const { openConfirmation } = useConfirmationStore();
  const teamId = team.id;
  const isPremium = team.isPremium;

  const handleDelete = () => {
    openConfirmation({
      title: 'Delete Team?',
      description: `Are you sure you want to delete this team? This action will remove all associated data, including team members and match statistics. It cannot be undone.`,
      cancelLabel: 'Cancel',
      actionLabel: 'Delete Team',
      onAction: () => {
        deleteTeamMutation.mutate(
          { teamId },
          {
            onSuccess: () => {
              onOpenChange(false); // Close the dialog
              toast.success('Team deleted successfully!');
            },
          },
        );
      },
      onCancel: () => {},
    });
  };

  return (
    <>
      <SettingsHeader
        title="Danger Zone"
        description="Actions in this section are permanent and cannot be undone."
        className="text-destructive"
      />
      <ScrollArea className="flex max-h-full flex-col px-6 py-4 pt-2">
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-x-4 rounded-lg border border-destructive/60 p-4">
            <div>
              <h3 className="text-sm font-semibold">Delete Team</h3>
              <p className="text-sm text-muted-foreground">
                Deleting this team will remove all associated data, including
                team members and match statistics.
              </p>
            </div>
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleteTeamMutation.isPending || isPremium}
                      size="sm"
                    >
                      {deleteTeamMutation.isPending
                        ? 'Deleting...'
                        : 'Delete Team'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isPremium && (
                  <TooltipContent className="max-w-xs">
                    <p>
                      You cannot delete this team while it has an active
                      subscription. Please cancel your subscription via the
                      billing portal and wait until the current period ends.
                      <button
                        className="ml-1 text-blue-500 hover:underline"
                        onClick={() => setActiveView('subscription')}
                      >
                        Go to Subscription
                      </button>
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        </div>
      </ScrollArea>
    </>
  );
}
